import { CreateGMWTask, EditGMWTask } from "@nens/brostar/uploadapi-client";
import { SchemaForFormData } from "@nens/shared-utils";

import { getDocsLinkMarkdown, getTitleAddon } from "@/libs/form-utils";
import { FormType } from "@/types/form-types";

import { pickGmwLibraryFields } from "../../../schema/gmw-schema-library";

import { monitoringTubeSchema } from "./gmw-monitoring-tube-schema";

const sharedTitle = (type: FormType | undefined) =>
  `GMW Grondwatermonitoring Put ${getTitleAddon(type)}`;
const sharedDescription = `Dit is het GMW constructie formulier. ${getDocsLinkMarkdown("GMW")}`;

const sharedSourcedocumentData = {
  type: "object" as const,
  title: "Brondocumentgegevens",
  required: [
    "objectIdAccountableParty",
    "deliveryContext",
    "constructionStandard",
    "initialFunction",
    "numberOfMonitoringTubes",
    "groundLevelStable",
    "owner",
    "wellHeadProtector",
    "wellConstructionDate",
    "deliveredLocation",
    "horizontalPositioningMethod",
    "localVerticalReferencePoint",
    "offset",
    "verticalDatum",
    "groundLevelPositioningMethod",
  ],
  properties: {
    ...pickGmwLibraryFields([
      "objectIdAccountableParty",
      "nitgCode",
      "deliveryContext",
      "constructionStandard",
      "initialFunction",
      "numberOfMonitoringTubes",
      "groundLevelStable",
      "owner",
      "maintenanceResponsibleParty",
      "wellHeadProtector",
      "wellConstructionDate",
      "deliveredLocation",
      "horizontalPositioningMethod",
      "localVerticalReferencePoint",
      "offset",
      "verticalDatum",
      "groundLevelPosition",
      "groundLevelPositioningMethod",
      "wellStability",
      "isAbroad",
      "geometricDataPubliclyAvailable",
      "relatedSurveys",
    ]),
    monitoringTubes: monitoringTubeSchema,
  },
};

const getGmwCreateSchema = (type: FormType | undefined): SchemaForFormData<CreateGMWTask> => ({
  type: "object",
  title: sharedTitle(type),
  description: sharedDescription,
  required: ["metadata", "sourcedocumentData"],
  properties: {
    metadata: {
      type: "object",
      title: "Metadata",
      required: ["projectNumber", "deliveryAccountableParty", "qualityRegime"],
      properties: pickGmwLibraryFields([
        "projectNumber",
        "qualityRegime",
        "deliveryAccountableParty",
      ]),
    },
    sourcedocumentData: sharedSourcedocumentData,
  },
});

const getGmwEditSchema = (type: FormType | undefined): SchemaForFormData<EditGMWTask> => ({
  type: "object",
  title: sharedTitle(type),
  description: sharedDescription,
  required: ["metadata", "sourcedocumentData"],
  properties: {
    metadata: {
      type: "object",
      title: "Metadata",
      required: ["projectNumber", "deliveryAccountableParty", "qualityRegime"],
      properties: {
        ...pickGmwLibraryFields([
          "projectNumber",
          "qualityRegime",
          "deliveryAccountableParty",
          "correctionReason",
        ]),
        broId: { ...pickGmwLibraryFields(["broId"]).broId, readOnly: true },
      },
    },
    sourcedocumentData: sharedSourcedocumentData,
  },
});

export function getGmwSchema(args: {
  type: FormType | undefined;
  isEditing: true;
}): SchemaForFormData<EditGMWTask>;
export function getGmwSchema(args: {
  type: FormType | undefined;
  isEditing: false;
}): SchemaForFormData<CreateGMWTask>;
export function getGmwSchema(args: {
  type: FormType | undefined;
  isEditing: boolean;
}): SchemaForFormData<EditGMWTask> | SchemaForFormData<CreateGMWTask>;
export function getGmwSchema({
  type,
  isEditing,
}: {
  type: FormType | undefined;
  isEditing: boolean;
}) {
  return isEditing ? getGmwEditSchema(type) : getGmwCreateSchema(type);
}
